export interface MountedShadowRoot {
  shadowRoot: ShadowRoot;
  styleEl: HTMLStyleElement;
  container: HTMLElement;
  unmount: () => void;
}

/**
 * Typing events that stop at the shadow root instead of continuing into the host page.
 *
 * The three key events close a FUNCTIONAL defect ([ADR 0008](../../../docs/decisions/0008-widget-contains-its-own-key-events.md)):
 * `keydown` decides whether a character is typed at all — a host handler calling
 * `preventDefault()` on it suppresses the insertion — so it is the only entry strictly
 * required to fix the originally reported bug. `keypress` (deprecated, still handled by
 * plenty of host pages) cancels insertion the same way. `keyup` cannot suppress anything,
 * but a host that advances on key release rather than key press would still act on the
 * widget's typing, and the rule is easier to reason about as "all three" than as "the two
 * that happen to be cancelable".
 *
 * `beforeinput` and `input` close a CONFIDENTIALITY one, and they are here on their own
 * argument rather than as an afterthought to ADR 0008 (see
 * [ADR 0011](../../../docs/decisions/0011-widget-contains-its-own-input-events.md)). They
 * are `composed` in exactly the way the key events are, but they carry the typed text
 * itself on `event.data`, so a host page binding either on `document` reads the free-text
 * body of a bug report character by character — the one field a reporter has most reason
 * to assume is confidential to Tenger. The trade is also different, which is why it is a
 * separate record: containing keys costs a host its own shortcuts while the dialog is
 * focused, defensible under `aria-modal` but a real cost; containing these two costs a
 * host nothing, because a host has no legitimate need to observe typing that happens
 * inside a third party's dialog.
 *
 * Containment cannot break the widget's own fields. `Attachments` and the description
 * field use Preact's `onInput`, which binds directly on the element and therefore runs
 * long before an event reaches this root, and nothing in `packages/widget/src` listens for
 * `beforeinput` at all.
 */
const CONTAINED_TYPING_EVENTS = ['keydown', 'keypress', 'keyup', 'beforeinput', 'input'] as const;

/**
 * Creates an isolated Shadow DOM mount point for the widget.
 *
 * A closed shadow root is used so host pages cannot reach into the widget's
 * internals via `element.shadowRoot`. Because a closed root isn't reflected
 * on the host element, callers must hold on to the returned `shadowRoot`
 * reference to interact with it (e.g. in tests).
 */
export function mountShadowRoot(hostEl?: HTMLElement): MountedShadowRoot {
  const isProvidedHost = Boolean(hostEl);
  const host = hostEl ?? document.createElement('div');

  if (!isProvidedHost) {
    // The screenshot capturer leaves this element, and so the whole widget,
    // out of every capture (`screenshot/capturer.ts`). Only on a host the widget
    // created: an element a caller handed in is theirs to mark or not.
    host.setAttribute('data-ft-host', '');
    document.body.appendChild(host);
  }

  const shadowRoot = host.attachShadow({ mode: 'closed' });

  const styleEl = document.createElement('style');
  shadowRoot.appendChild(styleEl);

  const container = document.createElement('div');
  shadowRoot.appendChild(container);

  // A closed shadow root hides the widget's DOM from the host page. It does NOT hide the
  // widget's keystrokes: key events are `composed`, so one typed into the description field
  // propagates out of this root and reaches whatever the host page has bound on
  // `document`/`window`. A `preventDefault()` from up there cancels the keydown's
  // text-insertion default action exactly as effectively as one at the field itself.
  //
  // `beforeinput`/`input` leave by the same route and are the worse half, because they carry
  // the typed characters on `event.data` rather than merely being able to suppress them: the
  // host does not have to interfere with the widget to harvest a bug report, only to listen.
  // See `CONTAINED_TYPING_EVENTS` above for why that argument stands on its own record.
  //
  // That is not hypothetical. Sightlines' status presentation binds a document keydown that
  // treats Space as "next slide" and calls `preventDefault()` on it
  // (`components/screens/admin-client-config/StatusPresentation.tsx:131-145`), so a space
  // typed into this widget never reached the textarea and advanced the deck instead. Note
  // what that handler does NOT have: any guard on `event.target`, `document.activeElement`,
  // `isContentEditable` or `closest('input,textarea')`. Shadow retargeting is therefore not
  // what broke it — a plain <textarea> rendered by Sightlines itself would lose Space the
  // same way. The widget's only contribution was letting the keystroke leave.
  //
  // Retargeting still decides where the fix can live. `mode: 'closed'` resolves both
  // `event.target` and `document.activeElement` to the host <div> above, so the guard shape a
  // host would reach for cannot see this textarea even when the host writes one. The host
  // cannot fix this for us; we can, once, for every host.
  //
  // The rule this encodes: a key pressed while focus is inside the widget belongs to the
  // widget. That covers more than the space character — the same handler was eating
  // ArrowLeft/ArrowRight caret movement in the field, and firing its own "exit the deck" on
  // the Escape that closed this modal.
  //
  // `stopPropagation`, never `stopImmediatePropagation`: `Modal` registers its own Esc
  // handler on this same shadow root (it resolves it with `getRootNode()`), after this one,
  // and has to keep running. And never `preventDefault`: every control in the widget is
  // activated by the browser's native Enter/Space -> synthetic click, the hidden file input
  // included, so cancelling the default would break all of them at once.
  //
  // Only events that ORIGINATE inside the widget travel through this node, so a host shortcut
  // pressed anywhere else on the page is untouched. Two limits worth naming rather than
  // discovering later: a host listener registered in the CAPTURE phase on `document`/`window`
  // runs before the event reaches this root, and nothing inside a shadow root can stop it
  // (only an iframe could, and the widget deliberately is not one); and `stopPropagation`
  // does not touch the user agent's own behaviour, so Cmd+C, Cmd+V, Cmd+A, F5 and the rest
  // still work inside the widget's fields. What it does silence is the HOST page's own
  // Ctrl/Cmd shortcuts while focus is in the widget, which is the intended trade.
  const containTypingEvent = (event: Event) => event.stopPropagation();
  for (const type of CONTAINED_TYPING_EVENTS) {
    shadowRoot.addEventListener(type, containTypingEvent);
  }

  const unmount = () => {
    // Removing the host would orphan these anyway, but teardown here is explicitly
    // symmetrical with mount — `bootstrap.test.ts` pins that add/remove parity for the
    // listeners the widget attaches outside its own components.
    for (const type of CONTAINED_TYPING_EVENTS) {
      shadowRoot.removeEventListener(type, containTypingEvent);
    }
    host.remove();
  };

  return { shadowRoot, styleEl, container, unmount };
}
